/* import __COLOCATED_TEMPLATE__ from './editor.hbs'; */
/* RESPONSIBLE TEAM: team-email */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import SidebarSection, {
  type SidebarSectionWireFormat,
} from 'embercom/objects/inbox/sidebar-section';
import type RoleComponent from 'embercom/models/roles/component';
import type InboxApi from 'embercom/services/inbox-api';
import { SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import { trackedFunction } from 'ember-resources/util/function';

interface Signature {
  Args: {
    sidebarSectionComponent: RoleComponent;
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

export default class ConfigurableComponentsEditor extends Component<Signature> {
  supportedSidebarSection = SupportedSidebarSection;
  @tracked openSectionId: string | null = null;
  @service declare inboxApi: InboxApi;

  get availableSidebarSections(): SidebarSection[] {
    return this.availableSectionsLoader.value ?? [];
  }

  get availableSectionsLoaded(): boolean {
    return !this.availableSectionsLoader.isLoading;
  }

  get pinnedSidebarSections(): SidebarSection[] {
    let pinnedSections = this.args.sidebarSectionComponent?.configuration.map(
      (section: SidebarSectionWireFormat) => SidebarSection.deserialize(section),
    );

    return pinnedSections || [];
  }

  @action onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  isPinned(section: SidebarSection): boolean {
    let sectionId = this.mapSidebarSectionToId(section);
    return !!sectionId && this.pinnedSectionIds().has(sectionId);
  }

  private pinnedSectionIds(): Set<string | number> {
    let ids = this.pinnedSidebarSections
      .map((section) => this.mapSidebarSectionToId(section))
      .filter((id): id is string | number => id !== undefined);
    return new Set(ids);
  }

  private mapSidebarSectionToId(section: SidebarSection): number | string | undefined {
    return section.intercomModuleIdentifier || section.inboxAppId;
  }

  private availableSectionsLoader = trackedFunction(this, async () => {
    return await this.inboxApi.listAvailableSidebarSections();
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teammates::Roles::ConfigurableComponents::Editor': typeof ConfigurableComponentsEditor;
    'settings/teammates/roles/configurable-components/editor': typeof ConfigurableComponentsEditor;
  }
}
